import React, { useState } from 'react';
import {
  Box,
  Button,
  InputAdornment,
  makeStyles,
  SvgIcon,
  TextField,
  Card,
  CircularProgress,
  withStyles,
  useTheme,
  useMediaQuery
} from '@material-ui/core';
import Slide from '@material-ui/core/Slide';
import Autocomplete from '@material-ui/lab/Autocomplete';
import moment from 'moment';
import PropTypes from 'prop-types';
import { Search as SearchIcon } from 'react-feather';
import { ArrowRight, ArrowLeft } from '@material-ui/icons';
import { useSelector } from 'react-redux';
import { DatePicker } from 'antd';
import styled from 'styled-components';
import { PAGE_SIZE_IMAGE } from 'src/app/constant/config';
import { Tabs, Tab } from '@material-ui/core';

const CustomTab = withStyles({
  root: {
    backgroundColor: '#F6F6F6',
    color: '#475461',
    fontSize: '14px',
    textTransform: 'none',
    minHeight: '48px',
  },
  selected: {
    backgroundColor: '#ffffff',
    color: '#D9042F',
    fontWeight: 600,
  }
})(Tab);

const StyledDatePicker = styled(DatePicker)`
  transition: border-color 0.3s ease;

  &:hover {
    border-color: #ee0434;
  }

  &.ant-picker-focused {
    border-color: #ee0434;
  }

  .ant-picker-input > input {
    height: 100%;
    line-height: 40px;
  }
`;

const HeaderToolBar = ({
  querySearch,
  searchRef,
  clearSearchRef,
  createNewDeviceRef,
  isLoading,
  setShowResult,
  toggleSidebar,
  isSidebarOpen,
  currentTab,
  onTabChange,
  ...rest
}) => {
  const classes = useStyles({ isSidebarOpen });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const formatDate = date => moment(date).format('YYYY-MM-DD');
  const formatTime = date => moment(date).format('HH:mm');
  const formatDateTime = (date, time) => {
    if (!date || !time) return '';
    return moment(`${formatDate(date)} ${time}`, 'YYYY-MM-DD HH:mm').format(
      'YYYY-MM-DDTHH:mm'
    );
  };

  const [query, setQuery] = useState({
    license_plate: '',
    serial: querySearch.serial || '',
    date: querySearch.start_date || moment(),
    start_time: querySearch.start_date ? moment(querySearch.start_date).format('HH:mm') : '00:00',
    end_time: querySearch.end_date ? moment(querySearch.end_date).format('HH:mm') : '23:59',
    camera: querySearch.camera || 'Tất cả camera'
  });

  const listLicensePlateCamera = useSelector(
    state => state.vehicleSlice.listLicensePlateCamera
  );
  const safeOptions = Array.isArray(listLicensePlateCamera) ? listLicensePlateCamera : [];

  const listVehicleTracking = useSelector(
    state => state.vehicleSlice.listVehicleTracking
  );

  React.useEffect(() => {
    if (query.serial && safeOptions.length > 0) {
      const selected = safeOptions.find(v => v.serial === query.serial);
      if (selected) {
        setQuery(prev => ({
          ...prev,
          license_plate: selected.license_plate,
          tenant: selected.tenant || selected.agency_id || ''
        }));
      }
    }
  }, [query.serial, safeOptions]);

  const [selectedHours, setSelectedHours] = useState(null);
  const [showCustomTime, setShowCustomTime] = useState(false);

  const cameraOptions = [
    { label: 'Tất cả camera', value: 'Tất cả camera' },
    { label: 'Camera 1', value: '101' },
    { label: 'Camera 2', value: '201' },
    { label: 'Camera 3', value: '301' },
    { label: 'Camera 4', value: '401' }
  ];

  const isAdmin = useSelector(state => state.authSlice.isAdmin);

  const isFormValid = () => {
    return (
      query.license_plate && query.date && query.start_time && query.end_time
    );
  };

  const onEnterSearchInput = event => {
    if (!searchRef) return;
    if (!query?.license_plate) return;
    if (event.keyCode === 13) {
      const queryValue = { ...query, page: 1 };
      setQuery(queryValue);
      const formattedQuery = {
        serial: queryValue.serial,
        start_date: formatDateTime(queryValue.date, queryValue.start_time),
        end_date: formatDateTime(queryValue.date, queryValue.end_time),
        camera: queryValue.camera === 'Tất cả camera' ? '' : queryValue.camera,
        tenant: queryValue.tenant || '',
        page: queryValue.page,
        page_size: PAGE_SIZE_IMAGE
      };
      if (
        formattedQuery.start_date &&
        formattedQuery.end_date &&
        isFormValid()
      ) {
        searchRef(formattedQuery);
        setShowResult(true);
        if (!selectedHours) {
          setSelectedHours('24H');
        }
      }
    }
  };

  const changeTextInputSearch = (event, value) => {
    if (typeof value === 'object') {
      const queryValue = {
        ...query,
        license_plate: value.license_plate,
        serial: value.serial,
        tenant: value.tenant || value.agency_id || '',
        page: 1
      };
      setQuery(queryValue);
    } else {
      const newValue = value || event?.target?.value || '';
      const queryValue = { ...query, license_plate: newValue, page: 1 };
      setQuery(queryValue);
    }
  };

  const changeDate = (value) => {
    const queryValue = {
      ...query,
      date: value,
      page: 1
    };
    setQuery(queryValue);
    setSelectedHours(null);
  };

  const changeStartTime = event => {
    const queryValue = {
      ...query,
      start_time: event.target.value,
      page: 1
    };
    setQuery(queryValue);
    setSelectedHours('Khác');
  };

  const changeEndTime = event => {
    const queryValue = {
      ...query,
      end_time: event.target.value,
      page: 1
    };
    setQuery(queryValue);
    setSelectedHours('Khác');
  };

  const changeCamera = (event, value) => {
    const selectedValue = value ? value.value : 'Tất cả camera';
    const queryValue = { ...query, camera: selectedValue, page: 1 };
    setQuery(queryValue);
  };

  const handleQuickTimeSelect = (hours, time) => {
    let queryValue = { ...query, page: 1 };
    const selectedDate = query.date
      ? moment(query.date, 'YYYY-MM-DD')
      : moment().startOf('day');
    const startOfSelectedDay = selectedDate.clone().startOf('day');
    const now = moment();
    const isToday = selectedDate.isSame(moment(), 'day');

    if (time === 'Khác') {
      queryValue.start_time = '00:00';
      queryValue.end_time = isToday ? formatTime(now) : '23:59';
      setShowCustomTime(true);
      setSelectedHours('Khác');
    } else {
      if (isToday) {
        queryValue.end_time = formatTime(now);
        const startTime = now.clone().subtract(hours, 'hours');
        queryValue.start_time = startTime.isBefore(startOfSelectedDay)
          ? '00:00'
          : formatTime(startTime);
      } else {
        queryValue.start_time = '00:00';
        queryValue.end_time =
          time === '24H'
            ? '23:59'
            : formatTime(startOfSelectedDay.clone().add(hours, 'hours'));
      }
      setShowCustomTime(false);
      setSelectedHours(time);
    }
    if (selectedDate.isAfter(moment(), 'day')) {
      queryValue.end_time = formatTime(now);
    }

    setQuery(queryValue);
  };

  const searchDevice = () => {
    if (!searchRef || !isFormValid()) {
      return;
    }
    const formattedQuery = {
      serial: query.serial,
      start_date: formatDateTime(query.date, query.start_time),
      end_date: formatDateTime(query.date, query.end_time),
      camera: query.camera === 'Tất cả camera' ? '' : query.camera,
      tenant: query.tenant || '',
      page: query.page || 1,
      page_size: PAGE_SIZE_IMAGE
    };
    if (formattedQuery.start_date && formattedQuery.end_date) {
      searchRef(formattedQuery);
      setShowResult(true);
      if (!selectedHours) {
        setSelectedHours('24H');
      }
    }
  };

  const onOk = value => { };

  return (
    <Box className={classes.root}>
      <Slide
        direction={isMobile ? 'down' : 'right'}
        in={isSidebarOpen}
        mountOnEnter
        unmountOnExit
      >
        <Card className={classes.sidebar}>
          <Tabs
            value={currentTab}
            onChange={onTabChange}
            variant="fullWidth"
            TabIndicatorProps={{ hidden: true }}
            style={{ minHeight: '48px' }}
          >
            <CustomTab label="Hình ảnh" />
            <CustomTab label="Xem lại video" />
          </Tabs>
          <Box className={classes.sidebarContent}>
            <div
              className={classes.labelChooseTime}
              style={{ marginBottom: '20px' }}
            >
              Chọn phương tiện và camera
            </div>
            <Box className={classes.fieldWrapper} display="flex" gap={8}>
              <Autocomplete
                freeSolo
                id="free-solo-2-demo"
                size="small"
                disableClearable
                options={safeOptions}
                getOptionLabel={option =>
                  typeof option === 'string' ? option : (option?.license_plate || '')
                }
                inputValue={query?.license_plate || ''}
                onChange={changeTextInputSearch}
                classes={{
                  inputRoot: classes.inputRoot,
                  input: classes.input,
                  option: classes.option
                }}
                renderInput={params => (
                  <CssTextField
                    {...params}
                    required
                    className={classes.searchInput}
                    InputProps={{
                      ...params.InputProps,
                      style: {
                        fontSize: '14px'
                      },
                      startAdornment: (
                        <InputAdornment position="start">
                          <SvgIcon fontSize="small" color="action" style={{ fontSize: '14px' }}>
                            <SearchIcon />
                          </SvgIcon>
                        </InputAdornment>
                      )
                    }}
                    variant="outlined"
                    placeholder="Nhập phương tiện"
                    onKeyDown={onEnterSearchInput}
                    onChange={changeTextInputSearch}
                  />
                )}
                style={{ flex: 1 }}
              />
              <Autocomplete
                id="camera-filter"
                size="small"
                classes={{
                  inputRoot: classes.inputRoot,
                  input: classes.input,
                  option: classes.option
                }}
                options={cameraOptions}
                getOptionLabel={option => option.label}
                value={
                  cameraOptions.find(
                    option => option.value === query.camera
                  ) || { label: 'Tất cả camera', value: 'Tất cả camera' }
                }
                onChange={changeCamera}
                disableClearable
                renderInput={params => (
                  <CssTextField
                    {...params}
                    variant="outlined"
                    placeholder="Tất cả camera"
                    InputProps={{
                      ...params.InputProps,
                      style: { fontSize: '14px' }
                    }}
                  />
                )}
                style={{ flex: 1 }}
              />
            </Box>

            <Box className={classes.fieldWrapper}>
              <div
                className={classes.labelChooseTime}
                style={{ marginBottom: '10px' }}
              >
                Chọn ngày và khoảng thời gian
              </div>
              <StyledDatePicker
                format={'DD-MM-YYYY'}
                value={query.date}
                onChange={changeDate}
                onOk={onOk}
                placeholder='Chọn thời gian'
                style={{
                  width: '352px',
                  height: '40px',
                  fontSize: '16px',
                  borderRadius: '4px'
                }}
              />
            </Box>

            <Box className={classes.fieldWrapper}>
              <Box
                display="grid"
                gridTemplateColumns="repeat(7, 1fr)"
                gap={8}
                width="100%"
              >
                {['1H', '4H', '8H', '12H', '24H', 'Khác'].map((time, index) => {
                  const hours = time === 'Khác' ? 0 : parseInt(time);
                  const isSelected = selectedHours === time;
                  return (
                    <Button
                      fullWidth
                      key={index}
                      variant="outlined"
                      size="small"
                      onClick={() => handleQuickTimeSelect(hours, time)}
                      style={{
                        fontSize: '14px',
                        padding: '4px 8px',
                        minWidth: '58px',
                        borderLeft: '0.5px solid #EBECED',
                        borderRight: '0.5px solid #EBECED',
                        borderTop: 'none',
                        borderBottom: 'none',
                        color: isSelected ? '#fff' : '#858C93',
                        backgroundColor: isSelected ? '#D9042F' : '#F5FAFF'
                      }}
                    >
                      {time}
                    </Button>
                  );
                })}
              </Box>
            </Box>

            {showCustomTime && (
              <Box className={classes.fieldWrapper}>
                <div className={classes.labelTime}>Từ giờ</div>
                <CssTextField
                  size="small"
                  fullWidth
                  margin="normal"
                  type="time"
                  name="start_time"
                  value={query.start_time}
                  onChange={changeStartTime}
                  variant="outlined"
                  InputProps={{
                    style: {
                      fontSize: '14px',
                      fontWeight: '400',
                      lineHeight: '140%',
                      color: '#34404B',
                      fontFamily: 'iCiel Helvetica Now Display'
                    }
                  }}
                />
                <div className={classes.labelTime}>Đến giờ</div>
                <CssTextField
                  size="small"
                  fullWidth
                  margin="normal"
                  type="time"
                  name="end_time"
                  value={query.end_time}
                  onChange={changeEndTime}
                  variant="outlined"
                  InputProps={{
                    style: {
                      fontSize: '14px',
                      fontWeight: '400',
                      lineHeight: '140%',
                      color: '#34404B',
                      fontFamily: 'iCiel Helvetica Now Display'
                    }
                  }}
                />
              </Box>
            )}

            <Box className={classes.fieldWrapper} position="relative">
              <Button
                fullWidth
                style={{
                  height: '48px',
                  background:
                    isLoading || !isFormValid() ? '#E0E0E0' : '#D9042F',
                  color: isLoading || !isFormValid() ? '#A0A5AA' : '#fff',
                  textTransform: 'inherit',
                  fontSize: '16px',
                  lineHeight: '20px',
                  fontFamily: 'iCiel Helvetica Now Display',
                  fontWeight: '500'
                }}
                disabled={!isFormValid() || isLoading}
                variant="contained"
                onClick={searchDevice}
              >
                {isLoading ? (
                  <>
                    <CircularProgress
                      size={24}
                      style={{ color: '#A0A5AA', marginRight: '8px' }}
                    />
                    <span>Vui lòng đợi ...</span>
                  </>
                ) : (
                  <span>{currentTab === 0 ? 'Tìm kiếm ảnh' : 'Tìm kiếm video'}</span>
                )}
              </Button>
            </Box>
          </Box>
          <Box
            className={classes.toggleButton}
            style={{ display: isSidebarOpen ? 'block' : 'none' }}
          >
            <Box
              title="Ẩn Menu"
              onClick={toggleSidebar}
              className={classes.centeredBox}
            >
              <ArrowLeft />
            </Box>
          </Box>
        </Card>
      </Slide>


      <Box
        className={classes.showMenuButton}
        style={{ display: isSidebarOpen ? 'none' : 'block' }}
      >
        <Box
          title="Hiện Menu"
          onClick={toggleSidebar}
          className={classes.centeredBox}
        >
          <ArrowRight />
        </Box>
      </Box>
    </Box>
  );
};

HeaderToolBar.propTypes = {
  className: PropTypes.string,
  searchRef: PropTypes.func,
  clearSearchRef: PropTypes.func,
  createNewDeviceRef: PropTypes.func,
  isLoading: PropTypes.bool,
  setShowResult: PropTypes.func,
  toggleSidebar: PropTypes.func,
  isSidebarOpen: PropTypes.bool,
  querySearch: PropTypes.object
};

const useStyles = makeStyles(theme => ({
  root: {
    position: 'fixed',
    top: 0,
    left: '0px',
    height: '100vh',
    zIndex: 1000
  },
  sidebar: {
    width: props => (props.isSidebarOpen ? '384px' : '0'),
    overflow: props => (props.isSidebarOpen ? 'auto' : 'hidden'),
    height: '100%',
    backgroundColor: '#FFFFFF',
    borderTop: '1px solid rgba(0,0,0,0.08)',
    boxShadow: 'rgba(99, 99, 99, 0.2) 0px 2px 8px 0px;',
    paddingTop: '50px',
    padding: props => (props.isSidebarOpen ? '66px 0px' : '0'),
    [theme.breakpoints.down('sm')]: {
      width: props =>
        props.isSidebarOpen
          ? '100%'
          : '0',
    }
  },
  sidebarContent: {
    padding: theme.spacing(2),
    borderTop: '1px solid rgba(0,0,0,0.08)'
  },
  toggleButton: {
    position: 'absolute',
    top: '90px',
    left: props => (props.isSidebarOpen ? '384px' : '0px'),
    zIndex: 999,
    backgroundColor: '#ffffff',
    borderRadius: '0 8px 8px 0',
    boxShadow: '0 1px 1px rgba(0,0,0,0.2)',
    width: '28px',
    height: '48px',
    cursor: 'pointer',
    transition: 'all 0.3s ease-in',
    [theme.breakpoints.down('sm')]: {
      left: props =>
        props.isSidebarOpen
          ? 'calc(100% - 30px)'
          : '0',
      top: '76px',
      height: '36px',
      borderRadius: '8px 0 0 8px',
    }
  },
  showMenuButton: {
    position: 'absolute',
    top: '90px',
    left: '0px',
    zIndex: 999,
    backgroundColor: '#ffffff',
    borderRadius: '0 8px 8px 0',
    boxShadow: '0 1px 1px rgba(0,0,0,0.2)',
    width: '28px',
    height: '48px',
    cursor: 'pointer',
    transition: 'all 0.3s ease-in',
    [theme.breakpoints.down('sm')]: {
      top: '76px',
      height: '36px',
    }
  },
  fieldWrapper: {
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
    gap: theme.spacing(1)
  },
  label: {
    marginBottom: '10px',
    fontSize: '14px',
    fontWeight: '500',
    fontFamily: 'iCiel Helvetica Now Display',
    color: '#0C1132'
  },
  labelTime: {
    lineHeight: '140%',
    fontSize: '14px',
    fontWeight: '500',
    fontFamily: 'iCiel Helvetica Now Display',
    color: '#0C1132'
  },
  labelChooseTime: {
    fontSize: '16px',
    fontWeight: '700',
    lineHeight: '20px',
    color: '#0C1132'
    //fontFamily: 'iCiel Helvetica Now Display',
  },
  centeredBox: {
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  input: {
    flex: 1,
    fontSize: '14px',
    padding: '0 !important',
    paddingLeft: '8px !important'
  },
  inputRoot: {
    height: 40,
    display: 'flex',
    alignItems: 'center',

    '& .MuiOutlinedInput-notchedOutline': {
      top: 0,
    },
  },
  option: {
    fontSize: '14px',
    borderBottom: '1px solid #e0e0e0',
    display: 'flex',
    alignItems: 'center',

    '&:last-child': {
      borderBottom: 'none'
    }
  },
}));

const CssTextField = withStyles({
  root: {
    '& .MuiInput-underline:after': {
      borderBottomColor: '#D6D9DB'
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: '#D6D9DB'
      },
      '&:hover fieldset': {
        borderColor: '#D9042F'
      },
      '&.Mui-focused fieldset': {
        borderColor: '#D9042F'
      }
    }
  }
})(TextField);

export default HeaderToolBar;
